import { Tab } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/16/solid';
import {
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import ErrorLogConsoleView from 'components/admin/error-log/error-log-console-view';
import ErrorLogErrorView from 'components/admin/error-log/error-log-error-view';
import ErrorLogMeta from 'components/admin/error-log/error-log-meta';
import ErrorLogScreenshotView from 'components/admin/error-log/error-log-screenshot-view';
import ErrorLogStateView from 'components/admin/error-log/error-log-state-view';
import ErrorLogTriggerView from 'components/admin/error-log/error-log-trigger-view';
import LoadingSpinner from 'components/common/loading-spinner';
import Modal from 'components/common/modal';
import Button from 'components/inputs/button';
import { useGetErrorLogQuery } from 'features/api/error-log-api';
import useCopiedFeedback from 'hooks/use-copied-feedback';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { formatErrorLogType } from 'utils/error-log/format';
import { buildReproduceUrl, canReproduce } from 'utils/error-log/reproduce';

type ErrorLogDetailsModalProps = {
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  errorLogId: number | null;
};

const TAB_NAMES = ['Error', 'Trigger', 'Console', 'Redux State'];

export default function ErrorLogDetailsModal({
  openState,
  errorLogId
}: ErrorLogDetailsModalProps) {
  const [open] = openState;
  const { copied, markCopied, resetCopied } = useCopiedFeedback();
  const { currentData: fetchedLog, isError } = useGetErrorLogQuery(
    errorLogId ?? 0,
    {
      skip: !open || errorLogId === null
    }
  );
  const log =
    fetchedLog !== undefined && fetchedLog.id === errorLogId
      ? fetchedLog
      : undefined;
  const showLoading = open && errorLogId !== null && !log && !isError;

  useEffect(() => {
    resetCopied();
  }, [errorLogId, open, resetCopied]);

  const title = log
    ? `Error Log #${log.id} — ${formatErrorLogType(log.errorType)}`
    : 'Error Log';

  const handleCopyJson = async () => {
    if (!log) {
      return;
    }
    await navigator.clipboard.writeText(JSON.stringify(log, null, 2));
    markCopied();
  };

  const handleReproduce = () => {
    if (!log) {
      return;
    }
    window.open(buildReproduceUrl(log), '_blank', 'noopener');
  };

  return (
    <Modal title={title} openState={openState} size='fullscreen' infoMode>
      <div className='select-text'>
        {showLoading && (
          <div className='flex justify-center py-10'>
            <LoadingSpinner />
          </div>
        )}
        {isError && !log && (
          <div className='py-6 text-center text-sm text-red-600'>
            Failed to load this error log.
          </div>
        )}
        {log && (
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-4 md:flex-row md:items-stretch'>
              <div className='md:w-80 md:shrink-0 lg:w-96'>
                <ErrorLogScreenshotView screenshot={log.screenshot} />
              </div>
              <div className='min-w-0 flex-1'>
                <ErrorLogMeta log={log} />
              </div>
            </div>
            <div className='flex flex-wrap justify-end gap-2'>
              <Button
                text={copied ? 'JSON Copied' : 'Copy JSON'}
                color='white'
                icon={copied ? CheckIcon : DocumentDuplicateIcon}
                onClick={handleCopyJson}
                disabled={copied}
              />
              <Button
                text='Reproduce'
                icon={ArrowTopRightOnSquareIcon}
                onClick={handleReproduce}
                disabled={!canReproduce(log)}
                tooltip={
                  canReproduce(log)
                    ? 'Open the logged URL and load the Redux snapshot (autosave disabled)'
                    : 'Reproduce is only available for errors logged in the main System Selector app'
                }
              />
            </div>
            <Tab.Group>
              <Tab.List className='flex gap-1 border-b border-gray-200'>
                {TAB_NAMES.map((name) => (
                  <Tab
                    key={name}
                    className={({ selected }) =>
                      clsx(
                        'px-4 py-2 text-sm font-medium focus:outline-none',
                        selected
                          ? 'border-b-2 border-blue-500 text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      )
                    }
                  >
                    {name}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className='max-h-[55vh] overflow-auto pt-4 focus:outline-none'>
                <Tab.Panel className='focus:outline-none'>
                  <ErrorLogErrorView log={log} />
                </Tab.Panel>
                <Tab.Panel className='focus:outline-none'>
                  <ErrorLogTriggerView trigger={log.triggerAction} />
                </Tab.Panel>
                <Tab.Panel className='focus:outline-none'>
                  <ErrorLogConsoleView entries={log.consoleSnapshot} />
                </Tab.Panel>
                <Tab.Panel className='focus:outline-none'>
                  <ErrorLogStateView snapshot={log.reduxState} />
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        )}
      </div>
    </Modal>
  );
}
